import { ref, computed, readonly, triggerRef, watch } from 'vue'
import type { PackingContainerId } from '@/constants/persoplan'

export type PackingContainerStatus = {
  id: `${PackingContainerId}.${string}`
  isPacking: boolean | undefined
}

export function usePackingMonitor() {
  const containers = ref(
    new Map<PackingContainerStatus['id'], PackingContainerStatus>()
  )

  function register(id: PackingContainerStatus['id']) {
    containers.value.set(id, { id, isPacking: undefined })
  }

  function unregister(id: PackingContainerStatus['id']) {
    containers.value.delete(id)
  }

  function updatePackingStatus(
    id: PackingContainerStatus['id'],
    isPacking: boolean
  ) {
    const container = containers.value.get(id)
    if (!container) return

    container.isPacking = isPacking
    triggerRef(containers)
  }

  function start(id: PackingContainerStatus['id']) {
    updatePackingStatus(id, true)
  }

  function stop(id: PackingContainerStatus['id']) {
    updatePackingStatus(id, false)
  }

  const isComplete = computed(() => {
    // Packing has not started yet, no elements registered
    if (containers.value.size === 0) {
      return false
    }

    for (const container of containers.value.values()) {
      // Packing of this element has not started
      if (container.isPacking === undefined) return false
      // If a single container is not packed, packing is not done
      if (container.isPacking === true) return false
    }

    return true
  })

  const provide = {
    register,
    unregister,
    start,
    stop,
  }

  return {
    isComplete: readonly(isComplete),
    provide,
  } as const
}

export type Provide = ReturnType<typeof usePackingMonitor>['provide']
